import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    HStack,
    Icon,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    Progress,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    TableContainer,
    useColorMode,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import {
    AiOutlineTrophy,
    AiFillTrophy,
    AiOutlineDollarCircle,
    AiOutlineCheckCircle,
    AiOutlineCloseCircle,
    AiOutlinePartition,
    AiOutlineSchedule,
    AiOutlineUser,
} from 'react-icons/ai';
import { useComercial } from '../../../../contexts/ComercialContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import api from '../../../../services/api';
import SearchableSelect from '../../../../components/UI/SearchableSelect';

const fmtMoney = (v) =>
    `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtBR = (iso) => {
    if (!iso) return '—';
    const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso);
};

const ROLE_COLORS = { admin: '#01E975', comercial: '#4299E1', suporte: '#9F7AEA', financeiro: '#ECC94B' };
const getRoleFromEmployee = (emp) => {
    if (emp?.isAdmin) return 'admin';
    if (emp?.isComercial) return 'comercial';
    if (emp?.is_MOBILE) return 'suporte';
    if (emp?.is_INTERNOS) return 'financeiro';
    return 'comercial';
};

const CANAL_COLORS = ['#01E975', '#4299E1', '#9F7AEA', '#ED8936', '#ECC94B', '#FC8181', '#48BB78', '#38B2AC', '#718096'];
const POSICAO = [{ cor: '#FFD700', label: '1º' }, { cor: '#C0C0C0', label: '2º' }, { cor: '#CD7F32', label: '3º' }];

const getInitials = (nome) =>
    !nome ? '?' : nome.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

const DesempenhoVendas = () => {
    const { pipelines } = useComercial();
    const { employees } = usePermissions();
    const { colorMode } = useColorMode();

    const [filtroFunil, setFiltroFunil] = useState('todos');
    const [filtroResponsavel, setFiltroResponsavel] = useState('todos');
    const [filtroCanal, setFiltroCanal] = useState('todos');
    const [filtroResultado, setFiltroResultado] = useState('todos');
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    const [canais, setCanais] = useState([]);

    const [data, setData] = useState({ kpis: { totalFechado: 0, vendas: 0, perdidos: 0, ticket: 0 }, vendedores: [], porCanal: [] });
    const [loading, setLoading] = useState(true);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [vendedorSel, setVendedorSel] = useState(null);

    const subtleBg = colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50';
    const trackBg = colorMode === 'dark' ? 'gray.700' : 'gray.200';
    const chartTooltipStyle = {
        backgroundColor: colorMode === 'dark' ? '#151719' : '#FFFFFF',
        borderColor: colorMode === 'dark' ? '#262930' : '#E2E8F0',
        borderRadius: '8px',
        color: colorMode === 'dark' ? '#FFFFFF' : '#000000',
    };
    // O recharts pinta o texto de cada item do tooltip com cor própria (preta por
    // padrão no Pie) — sem itemStyle/labelStyle o texto some no dark mode.
    const chartTooltipItemStyle = { color: colorMode === 'dark' ? '#FFFFFF' : '#000000' };
    const chartTooltipLabelStyle = { color: colorMode === 'dark' ? '#A0A3A8' : '#4A5568' };

    useEffect(() => {
        api.get('/comercial/dropdowns/canais').then((r) => setCanais(r.data || [])).catch(() => { });
    }, []);

    // Busca o desempenho no backend (agregação direta nas tabelas) sempre que um filtro muda
    useEffect(() => {
        let ativo = true;
        setLoading(true);
        const params = {};
        if (filtroFunil !== 'todos') params.funilId = filtroFunil;
        if (filtroResponsavel !== 'todos') params.assigneeId = filtroResponsavel;
        if (filtroCanal !== 'todos') params.canal = filtroCanal;
        if (filtroResultado !== 'todos') params.resultado = filtroResultado;
        if (filtroDataInicio) params.dataInicio = filtroDataInicio;
        if (filtroDataFim) params.dataFim = filtroDataFim;

        api.get('/comercial/metas/desempenho', { params })
            .then((r) => { if (ativo) setData(r.data || { kpis: {}, vendedores: [], porCanal: [] }); })
            .catch((err) => { console.error('Erro ao carregar desempenho:', err); })
            .finally(() => { if (ativo) setLoading(false); });

        return () => { ativo = false; };
    }, [filtroFunil, filtroResponsavel, filtroCanal, filtroResultado, filtroDataInicio, filtroDataFim]);

    // Enriquecimento visual (cor por role + e-mail) a partir dos colaboradores
    const empById = useMemo(() => {
        const mapa = {};
        (employees || []).forEach((e) => { mapa[String(e.id)] = e; });
        return mapa;
    }, [employees]);

    const enriquecer = useCallback((v) => {
        const emp = v.assignee_id != null ? empById[String(v.assignee_id)] : null;
        const role = emp ? getRoleFromEmployee(emp) : 'comercial';
        return { ...v, email: emp?.email || '', cor: ROLE_COLORS[role] || ROLE_COLORS.comercial };
    }, [empById]);

    const vendedores = useMemo(() => (data.vendedores || []).map(enriquecer), [data.vendedores, enriquecer]);
    const kpis = data.kpis || {};
    const topVendedor = vendedores.find((v) => v.valorGanho > 0) || null;

    const barData = useMemo(
        () => vendedores.filter((v) => v.valorGanho > 0).slice(0, 8).map((v) => ({ name: (v.nome || '').split(' ')[0], value: v.valorGanho, cor: v.cor })),
        [vendedores]
    );
    const canalData = useMemo(
        () => (data.porCanal || []).map((d, i) => ({ ...d, cor: CANAL_COLORS[i % CANAL_COLORS.length] })),
        [data.porCanal]
    );

    const podio = vendedores.filter((v) => v.valorGanho > 0).slice(0, 3);
    const ordemPodio = podio.length === 3 ? [podio[1], podio[0], podio[2]] : podio;

    const abrirVendedor = useCallback((v) => { setVendedorSel(v); onOpen(); }, [onOpen]);

    const limparFiltros = () => {
        setFiltroFunil('todos'); setFiltroResponsavel('todos'); setFiltroCanal('todos');
        setFiltroResultado('todos'); setFiltroDataInicio(''); setFiltroDataFim('');
    };

    const semDados = !loading && vendedores.length === 0;

    return (
        <VStack align="stretch" spacing={6}>
            {/* Filtros (mesmo padrão da tela Relatórios) */}
            <Box p={5} bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl">
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(7, 1fr)' }} gap={4} alignItems="end">
                    <GridItem>
                        <FieldLabel>Funil</FieldLabel>
                        <SearchableSelect size="sm" value={filtroFunil} onChange={v => setFiltroFunil(v == null ? 'todos' : v)}
                            options={[{ value: 'todos', label: 'Todos os funis' }, ...(pipelines || []).map(p => ({ value: p.id, label: p.nome }))]}
                            placeholder="Buscar funil..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Responsável</FieldLabel>
                        <SearchableSelect size="sm" value={filtroResponsavel} onChange={v => setFiltroResponsavel(v == null ? 'todos' : v)}
                            options={[{ value: 'todos', label: 'Todos' }, ...employees.map(emp => ({ value: emp.id, label: emp.name }))]}
                            placeholder="Buscar responsável..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Canal</FieldLabel>
                        <SearchableSelect size="sm" value={filtroCanal} onChange={v => setFiltroCanal(v == null ? 'todos' : v)}
                            options={[{ value: 'todos', label: 'Todos' }, ...canais.map(c => ({ value: c.nome, label: c.nome }))]}
                            placeholder="Buscar canal..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Resultado</FieldLabel>
                        <SearchableSelect size="sm" value={filtroResultado} onChange={v => setFiltroResultado(v == null ? 'todos' : v)}
                            options={[
                                { value: 'todos', label: 'Todos' },
                                { value: 'ganho', label: 'Ganho' },
                                { value: 'perdido', label: 'Perdido' },
                                { value: 'aberto', label: 'Em aberto' },
                            ]}
                            placeholder="Buscar resultado..." {...selectStyle} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Data Início</FieldLabel>
                        <Input type="date" size="sm" value={filtroDataInicio} onChange={(e) => setFiltroDataInicio(e.target.value)} {...dateStyle(colorMode)} />
                    </GridItem>
                    <GridItem>
                        <FieldLabel>Data Fim</FieldLabel>
                        <Input type="date" size="sm" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} {...dateStyle(colorMode)} />
                    </GridItem>
                    <GridItem>
                        <Button size="sm" width="100%" variant="outline" borderColor="borderPrimary" color="textSecondary" leftIcon={<Icon as={AiOutlineCloseCircle} />}
                            _hover={{ bg: 'surfaceHover', color: 'textPrimary', borderColor: 'textSecondary' }} onClick={limparFiltros}>
                            Limpar
                        </Button>
                    </GridItem>
                </Grid>
            </Box>

            {/* KPIs */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <KpiCard icon={AiOutlineDollarCircle} color="#01E975" label="Total Fechado" value={fmtMoney(kpis.totalFechado)} sub="valor ganho" />
                <KpiCard icon={AiOutlineCheckCircle} color="#4299E1" label="Vendas Fechadas" value={fmtInt(kpis.vendas)} sub={`${fmtInt(kpis.perdidos)} perdidos`} />
                <KpiCard icon={AiOutlineTrophy} color="#ECC94B" label="Ticket Médio" value={fmtMoney(kpis.ticket)} sub="por venda ganha" />
                <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={4}>
                    <HStack justify="space-between" mb={2}>
                        <Text fontSize="10px" color="textMuted" textTransform="uppercase" letterSpacing="1px" fontWeight="800">Top Vendedor</Text>
                        <Icon as={AiFillTrophy} color="#FFD700" boxSize="18px" />
                    </HStack>
                    {topVendedor ? (
                        <HStack spacing={3}>
                            <Avatar size="sm" name={topVendedor.nome} bg={`${topVendedor.cor}25`} color={topVendedor.cor} fontWeight="900" fontSize="xs">{getInitials(topVendedor.nome)}</Avatar>
                            <VStack align="start" spacing={0} minW={0}>
                                <Text fontSize="md" fontWeight="900" color="textPrimary" isTruncated maxW="120px">{topVendedor.nome}</Text>
                                <Text fontSize="xs" color="secondary" fontWeight="800">{fmtMoney(topVendedor.valorGanho)}</Text>
                            </VStack>
                        </HStack>
                    ) : <Text fontSize="sm" color="textMuted">—</Text>}
                </Box>
            </SimpleGrid>

            {loading ? (
                <Flex justify="center" align="center" py={20} gap={3} color="textSecondary">
                    <Spinner color="secondary" /> <Text fontSize="sm">Carregando desempenho...</Text>
                </Flex>
            ) : semDados ? (
                <EmptyState />
            ) : (
                <>
                    {/* Pódio */}
                    {podio.length > 0 && (
                        <ChartCard title="Pódio de Vendas">
                            <SimpleGrid columns={{ base: 1, md: podio.length }} spacing={4} alignItems="end">
                                {ordemPodio.map((v) => {
                                    const idx = podio.indexOf(v);
                                    const pos = POSICAO[idx];
                                    const destaque = idx === 0;
                                    return (
                                        <Box key={v.assignee_id ?? v.nome} bg={subtleBg} border="1px solid" borderColor={destaque ? pos.cor : 'borderPrimary'} borderRadius="xl"
                                            p={destaque ? 6 : 5} position="relative" overflow="hidden" cursor="pointer" onClick={() => abrirVendedor(v)}
                                            transform={destaque ? 'scale(1.02)' : 'none'}
                                            _hover={{ borderColor: pos.cor, transform: destaque ? 'scale(1.04)' : 'translateY(-2px)' }} transition="all 0.2s">
                                            <Box position="absolute" top={0} left={0} right={0} h="3px" bg={pos.cor} />
                                            <VStack spacing={3}>
                                                <HStack spacing={2}>
                                                    <Icon as={destaque ? AiFillTrophy : AiOutlineTrophy} color={pos.cor} boxSize={destaque ? '24px' : '18px'} />
                                                    <Badge bg={`${pos.cor}20`} color={pos.cor} fontWeight="900" fontSize="xs" borderRadius="md">{pos.label} lugar</Badge>
                                                </HStack>
                                                <Avatar size={destaque ? 'lg' : 'md'} name={v.nome} bg={`${v.cor}25`} color={v.cor} fontWeight="900" borderWidth="2px" borderColor={pos.cor}>{getInitials(v.nome)}</Avatar>
                                                <VStack spacing={0}>
                                                    <Text fontSize={destaque ? 'lg' : 'md'} fontWeight="900" color="textPrimary" textAlign="center" noOfLines={1}>{v.nome}</Text>
                                                    <Text fontSize={destaque ? 'xl' : 'lg'} fontWeight="900" color={pos.cor} lineHeight="1.2">{fmtMoney(v.valorGanho)}</Text>
                                                </VStack>
                                                <HStack spacing={4} pt={1}>
                                                    <VStack spacing={0}><Text fontSize="sm" fontWeight="900" color="textPrimary">{v.ganho}</Text><Text fontSize="9px" color="textSecondary" textTransform="uppercase">Vendas</Text></VStack>
                                                    <VStack spacing={0}><Text fontSize="sm" fontWeight="900" color="textPrimary">{(v.conversao || 0).toFixed(0)}%</Text><Text fontSize="9px" color="textSecondary" textTransform="uppercase">Conversão</Text></VStack>
                                                </HStack>
                                            </VStack>
                                        </Box>
                                    );
                                })}
                            </SimpleGrid>
                        </ChartCard>
                    )}

                    {/* Gráficos */}
                    <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
                        <ChartCard title="Valor Fechado por Vendedor">
                            {barData.length === 0 ? <EmptyChart /> : (
                                <Box h="280px" w="100%">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={barData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={colorMode === 'dark' ? '#262920' : '#E2E8F0'} vertical={false} />
                                            <XAxis dataKey="name" stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} />
                                            <YAxis stroke={colorMode === 'dark' ? '#718096' : '#4A5568'} fontSize={10} tickLine={false} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                                            <ChartTooltip contentStyle={chartTooltipStyle} itemStyle={chartTooltipItemStyle} labelStyle={chartTooltipLabelStyle} cursor={{ fill: 'transparent' }} formatter={(v) => fmtMoney(v)} />
                                            <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={28} name="Fechado">
                                                {barData.map((entry, i) => <Cell key={i} fill={entry.cor} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </ChartCard>

                        <ChartCard title="Origem das Vendas">
                            {canalData.length === 0 ? <EmptyChart /> : (
                                <Box h="280px" w="100%">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={canalData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                                                {canalData.map((entry, i) => <Cell key={i} fill={entry.cor} />)}
                                            </Pie>
                                            <ChartTooltip contentStyle={chartTooltipStyle} itemStyle={chartTooltipItemStyle} labelStyle={chartTooltipLabelStyle} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </ChartCard>
                    </Grid>

                    {/* Ranking */}
                    <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                        <HStack px={5} py={4} borderBottom="1px solid" borderColor="borderPrimary">
                            <Icon as={AiOutlineTrophy} color="secondary" />
                            <Text fontWeight="800" color="textPrimary">Ranking de Vendedores</Text>
                        </HStack>
                        <TableContainer>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <ThCell>#</ThCell>
                                        <ThCell>Vendedor</ThCell>
                                        <ThCell isNumeric>Vendas</ThCell>
                                        <ThCell isNumeric>Valor Fechado</ThCell>
                                        <ThCell isNumeric>Ticket Médio</ThCell>
                                        <ThCell isNumeric>Perdidos</ThCell>
                                        <ThCell isNumeric>Atividades</ThCell>
                                        <ThCell>Conversão</ThCell>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {vendedores.map((v, i) => (
                                        <Tr key={v.assignee_id ?? v.nome} cursor="pointer" _hover={{ bg: subtleBg }} onClick={() => abrirVendedor(v)}>
                                            <Td borderColor="borderPrimary">
                                                {i < 3 && v.valorGanho > 0 ? <Icon as={i === 0 ? AiFillTrophy : AiOutlineTrophy} color={POSICAO[i].cor} boxSize="16px" /> : <Text fontSize="sm" color="textSecondary" fontWeight="800">{i + 1}</Text>}
                                            </Td>
                                            <Td borderColor="borderPrimary">
                                                <HStack spacing={3}>
                                                    <Avatar size="sm" name={v.nome} bg={`${v.cor}25`} color={v.cor} fontWeight="900" fontSize="xs">{getInitials(v.nome)}</Avatar>
                                                    <VStack align="start" spacing={0} minW={0}>
                                                        <Text fontWeight="800" fontSize="sm" color="textPrimary" isTruncated maxW="180px">{v.nome}</Text>
                                                        {v.email && <Text fontSize="10px" color="textSecondary" isTruncated maxW="180px">{v.email}</Text>}
                                                    </VStack>
                                                </HStack>
                                            </Td>
                                            <Td borderColor="borderPrimary" isNumeric><Badge bg="rgba(66,153,225,0.15)" color="#4299E1" fontWeight="800">{v.ganho}</Badge></Td>
                                            <Td borderColor="borderPrimary" isNumeric><Text fontSize="sm" fontWeight="800" color={v.valorGanho > 0 ? 'secondary' : 'textMuted'}>{fmtMoney(v.valorGanho)}</Text></Td>
                                            <Td borderColor="borderPrimary" isNumeric><Text fontSize="sm" color="textSecondary">{v.ticket > 0 ? fmtMoney(v.ticket) : '—'}</Text></Td>
                                            <Td borderColor="borderPrimary" isNumeric><Text fontSize="sm" color="textSecondary">{v.perdido || '—'}</Text></Td>
                                            <Td borderColor="borderPrimary" isNumeric><Text fontSize="sm" color="textSecondary">{v.atividades || '—'}</Text></Td>
                                            <Td borderColor="borderPrimary">
                                                <HStack spacing={2}>
                                                    <Box w="50px"><Progress value={v.conversao || 0} size="xs" colorScheme="green" borderRadius="full" bg={trackBg} /></Box>
                                                    <Text fontSize="xs" color="textSecondary">{(v.conversao || 0).toFixed(0)}%</Text>
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            )}

            {/* Modal ficha do vendedor */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.700" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl" overflow="hidden">
                    <ModalCloseButton color="textSecondary" zIndex={2} />
                    <ModalBody pt={0} pb={2} px={6}>
                        {vendedorSel && (
                            <VStack spacing={5} align="stretch">
                                <Box mx={-6} pt={10} pb={5} px={6} bgGradient={`linear(to-b, ${vendedorSel.cor}26, transparent)`}>
                                    <Flex direction="column" align="center" gap={3}>
                                        <Avatar size="xl" name={vendedorSel.nome} bg={`${vendedorSel.cor}25`} color={vendedorSel.cor} fontWeight="900" fontSize="2xl">{getInitials(vendedorSel.nome)}</Avatar>
                                        <VStack spacing={1}>
                                            <Text fontSize="xl" fontWeight="900" color="textPrimary">{vendedorSel.nome}</Text>
                                            {vendedorSel.email && (
                                                <HStack spacing={1} color="textSecondary"><Icon as={AiOutlineUser} boxSize="11px" /><Text fontSize="xs">{vendedorSel.email}</Text></HStack>
                                            )}
                                        </VStack>
                                    </Flex>
                                </Box>

                                <SimpleGrid columns={2} spacing={3}>
                                    <DetField icon={AiOutlineDollarCircle} label="Total Fechado" accent="#01E975" value={fmtMoney(vendedorSel.valorGanho)} />
                                    <DetField icon={AiOutlineCheckCircle} label="Vendas" accent="#4299E1" value={fmtInt(vendedorSel.ganho)} />
                                    <DetField icon={AiOutlineTrophy} label="Ticket Médio" accent="#ECC94B" value={vendedorSel.ticket > 0 ? fmtMoney(vendedorSel.ticket) : '—'} />
                                    <DetField icon={AiOutlineSchedule} label="Atividades" accent="#9F7AEA" value={fmtInt(vendedorSel.atividades)} />
                                </SimpleGrid>

                                <Divider borderColor="borderPrimary" />

                                <Box>
                                    <Text fontSize="xs" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={2}>
                                        Negócios fechados ({(vendedorSel.negocios || []).length})
                                    </Text>
                                    {(vendedorSel.negocios || []).length === 0 ? (
                                        <Text fontSize="sm" color="textMuted" py={4} textAlign="center">Nenhuma venda ganha no filtro atual.</Text>
                                    ) : (
                                        <VStack align="stretch" spacing={2}>
                                            {vendedorSel.negocios.map((n) => (
                                                <HStack key={n.id} bg={subtleBg} px={3} py={2} borderRadius="md" border="1px solid" borderColor="borderPrimary" justify="space-between">
                                                    <VStack align="start" spacing={0} minW={0}>
                                                        <Text fontSize="sm" fontWeight="800" color="textPrimary" isTruncated maxW="260px">{n.titulo || 'Negócio'}</Text>
                                                        <Text fontSize="xs" color="textSecondary">{n.empresa || '—'}</Text>
                                                    </VStack>
                                                    <VStack align="end" spacing={0} flexShrink={0}>
                                                        <Text fontSize="sm" fontWeight="900" color="secondary">{fmtMoney(n.valor)}</Text>
                                                        <Text fontSize="10px" color="textSecondary">{fmtBR(n.dataFechamento)}</Text>
                                                    </VStack>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    )}
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" mt={2}>
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: subtleBg }} onClick={onClose} size="sm">Fechar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
};

// ── Sub-componentes (mesmos padrões da tela Relatórios) ──
const FieldLabel = ({ children }) => (
    <Text fontSize="xs" fontWeight="800" color="textSecondary" mb={1.5} textTransform="uppercase" letterSpacing="0.5px">{children}</Text>
);
const selectStyle = { bg: 'surfaceHover', borderColor: 'borderPrimary', borderRadius: 'lg', _focus: { borderColor: 'secondary', boxShadow: 'none' } };
const dateStyle = (colorMode) => ({
    bg: 'surfaceHover', borderColor: 'borderPrimary', borderRadius: 'lg', color: 'textPrimary',
    css: { '&::-webkit-calendar-picker-indicator': { filter: colorMode === 'dark' ? 'invert(1)' : 'none' } },
    _focus: { borderColor: 'secondary', boxShadow: 'none' },
});

const KpiCard = ({ icon, color, label, value, sub }) => (
    <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={4}>
        <HStack justify="space-between" mb={2}>
            <Text fontSize="10px" color="textMuted" textTransform="uppercase" letterSpacing="1px" fontWeight="800">{label}</Text>
            <Icon as={icon} color={color} boxSize="18px" />
        </HStack>
        <Text fontSize="2xl" fontWeight="900" color="textPrimary">{value}</Text>
        {sub && <Text fontSize="10px" color="textSecondary" mt={1}>{sub}</Text>}
    </Box>
);

const ChartCard = ({ title, children }) => (
    <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={5}>
        <Text fontWeight="800" color="textPrimary" fontSize="md" mb={4}>{title}</Text>
        {children}
    </Box>
);

const ThCell = ({ children, ...p }) => (
    <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" {...p}>{children}</Th>
);

const DetField = ({ icon, label, value, accent = '#718096' }) => {
    const { colorMode } = useColorMode();
    return (
        <Box bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50'} border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3}>
            <HStack spacing={2} mb={1}>
                {icon && <Icon as={icon} color={accent} boxSize="12px" />}
                <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px">{label}</Text>
            </HStack>
            <Text fontSize="md" color="textPrimary" fontWeight="800">{value || '—'}</Text>
        </Box>
    );
};

const EmptyChart = () => (
    <Flex h="280px" align="center" justify="center"><Text color="textSecondary" fontSize="sm">Sem dados no filtro atual.</Text></Flex>
);

const EmptyState = () => (
    <Box bg="surface" border="1px dashed" borderColor="borderPrimary" borderRadius="xl" p={12} textAlign="center">
        <Icon as={AiOutlinePartition} color="textMuted" boxSize="36px" mb={3} />
        <Text color="textMuted" fontSize="sm" fontWeight="700">Nenhum negócio concluído no filtro atual.</Text>
        <Text color="textMuted" fontSize="xs" mt={1}>Os negócios aparecem aqui quando movidos para uma coluna de Ganho/Perdido no Pipeline.</Text>
    </Box>
);

export default DesempenhoVendas;
